import { LayoutDashboard } from "lucide-react";
import { useDashboards } from "@/hooks/dashboard/useDashboards";
import type { DashboardRecord } from "@/lib/dashboard/types";
import type { SidebarItem } from "@/types/navigation";
import { isKnownPlacementTarget } from "@/config/navigationRegistry";
import { REPORTS_NAV } from "@/pages/measurement/reportsNav";
import { useAuthorization } from "@/contexts/AuthorizationContext";
import { requiresLiveAccess } from "@/lib/dashboard/composition";

export type DashboardPlacementSection = "measurement";

export function buildPlacementNav(
  section: DashboardPlacementSection,
  dashboards: readonly DashboardRecord[],
  canViewAnalytics = true,
): SidebarItem[] {
  const base = REPORTS_NAV;
  const placed = dashboards
    .filter(
      (dashboard) =>
        dashboard.kind !== "template" &&
        dashboard.state !== "draft" &&
        (canViewAnalytics || !requiresLiveAccess(dashboard.config)),
    )
    .flatMap((dashboard) =>
      (dashboard.placements ?? [])
        .filter(
          (placement) =>
            placement.section_id === section &&
            placement.group_id === "dashboards" &&
            isKnownPlacementTarget(placement.section_id, placement.group_id),
        )
        .map((placement) => ({ dashboard, placement })),
    )
    .sort(
      (a, b) =>
        (a.placement.order ?? Number.MAX_SAFE_INTEGER) -
          (b.placement.order ?? Number.MAX_SAFE_INTEGER) ||
        a.dashboard.name.localeCompare(b.dashboard.name),
    );
  const unique = [...new Map(placed.map(({ dashboard }) => [dashboard.id, dashboard])).values()];
  if (!unique.length) return base;
  const children: SidebarItem[] = unique.map((dashboard) => ({
    id: `${section}-dashboard-${dashboard.id}`,
    label: dashboard.name,
    href: `/${section}/dashboards/${encodeURIComponent(dashboard.slug)}`,
    icon: LayoutDashboard,
  }));
  return [
    ...base,
    {
      id: `${section}-dashboards`,
      label: "Dashboards",
      href: children[0].href,
      icon: LayoutDashboard,
      children,
    },
  ];
}

export function usePlacementNav(section: DashboardPlacementSection): SidebarItem[] {
  const { canAccessSection } = useAuthorization();
  const dashboards = useDashboards({ relationship: "accessible", state: "saved" });
  return buildPlacementNav(section, dashboards.data ?? [], canAccessSection("live"));
}
